import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'ramda';
import { Message } from 'mage-i18n';
import { VerticalRhythm } from 'mage-components';

import {
  constants,
  ParagraphQuaternary,
  ParagraphSecondary,
  ParagraphTertiary,
  SelectorDirect,
} from '@klarna/bubble-ui';

import { DESIRED_PRODUCT_SORTING } from '../constants';

import CheckMarkRound from './CheckMarkRound';
import ProductHead from './ProductHead';
import ProductPricing from './ProductPricing';

import { isProductMandatory } from '../utils/products';

const tPrefix = 'product-selection-container.products';
const tPrefixProducts = 'moet-core.products';

const styles = {
  grayLine: {
    borderTopColor: 'rgb(240, 238, 235)',
    paddingBottom: '0px',
    borderStyle: 'solid',
    borderWidth: '1px 0px 0px',
  },
};

const badgeStyles = ({ isBadgeActive }) => ({
  backgroundColor: isBadgeActive ? constants.Colors.BLACK : constants.Colors.GRAY,
  padding: '5px',
  display: 'inline-block',
  marginBottom: '10px',
  transition: 'all 400ms ease-out',
});

class Products extends React.Component {
  constructor(props) {
    super(props);
    this.state = { isBadgeActive: false };

    this.onSelect = this.onSelect.bind(this);
  }

  static generateProductsForState(countryKey, products, isSelectedFn, isInstalledFn = () => false) {
    const addGroupToTheProduct = id => (id === 'kco' ? 'kco' : 'kp');
    const priceFromProduct = ({ prices = [] } = {}) =>
      prices.find(elem => elem.consumerCountry === countryKey) || {};

    return products
      .reduce((acc, elem) => [...acc, ...(elem.pricing === 'CHILDREN' ? elem.options : [elem])], [])
      .reduce((acc, elem) => {
        acc[elem.id] = {
          ...elem,
          value: elem.id,
          isSelected: isSelectedFn(elem.id),
          isInstalled: isInstalledFn(elem.id),
          group: addGroupToTheProduct(elem.id),
          price: priceFromProduct(elem),
        };

        return acc;
      }, {});
  }

  static generateFilteredProducts(
    products = {},
    t,
    groupName = '',
    country,
    isAddingProducts,
    isKlarnaOneAvailable = false,
    isBadgeActive = false,
    extraAttributesToProductFn,
  ) {
    return (
      Object.keys(products)
        .map(key => products[key])
        .filter(product => product.group === groupName && !product.hideFromSelection)
        .map(product => {
          const disabled =
            !product.isCompatible || product.isInstalled || product.notAvailableForBusiness;
          const isMandatory = isProductMandatory(product.id, country, { isKlarnaOneAvailable });
          const isCheckmarkDisabled = isAddingProducts || disabled || isMandatory;

          const hasPrice = !isEmpty(product.price);
          const name =
            t(`${tPrefixProducts}.${product.name}.${country}`) ||
            t(`${tPrefixProducts}.${product.name}.default`);

          const description =
            t(`${tPrefixProducts}.${product.description}.${country}`) ||
            t(`${tPrefixProducts}.${product.description}.default`);

          const label = (
            <>
              {isMandatory && (
                <div style={badgeStyles({ isBadgeActive })}>
                  <ParagraphTertiary correctFootprint inverseOnBackground={isBadgeActive}>
                    {t(`${tPrefixProducts}.mandatory`)}
                  </ParagraphTertiary>
                </div>
              )}
              <ProductHead productId={product.id} productName={name} />
            </>
          );

          return {
            key: product.value,
            value: product.value,
            isChecked: product.isSelected,
            disabled,
            rightIcon: (
              <CheckMarkRound isChecked={product.isSelected} disabled={isCheckmarkDisabled} />
            ),
            label,
            description: (
              <>
                <VerticalRhythm spacing={3} />
                <ParagraphSecondary correctFootprint disabled={disabled} light>
                  {description}
                </ParagraphSecondary>
                <VerticalRhythm spacing={3} />
                {!product.isInstalled &&
                  hasPrice &&
                  product.isCompatible &&
                  !product.notAvailableForBusiness && (
                    <ProductPricing price={product.price} disabled={disabled} />
                  )}
                <ParagraphTertiary disabled={disabled} correctFootprint medium light>
                  {product.isInstalled && (
                    <>
                      *
                      <Message path={`${tPrefix}.products_block.installed`} />*
                    </>
                  )}
                  {product.notAvailableForBusiness && product.isCompatible && (
                    <>
                      *
                      <Message path={`${tPrefix}.products_block.not_available_for_business`} />*
                    </>
                  )}
                  {!product.notAvailableForBusiness && !hasPrice && product.isCompatible && (
                    <>
                      *
                      <Message path={`${tPrefix}.products_block.not_available_for_country`} />*
                    </>
                  )}
                  {!product.isCompatible && (
                    <>
                      *
                      <Message path={`${tPrefix}.products_block.not_compatible_with_platform`} />*
                    </>
                  )}
                </ParagraphTertiary>
              </>
            ),
            ...extraAttributesToProductFn(product),
          };
        })
        // sorting has to be in the component because of the Safari browser
        .sort((a, b) => {
          const indexA = DESIRED_PRODUCT_SORTING.indexOf(a.value);
          const indexB = DESIRED_PRODUCT_SORTING.indexOf(b.value);
          if (indexA < 0) {
            return 1;
          }
          if (indexB < 0) {
            return -1;
          }
          return indexA - indexB;
        })
    );
  }

  onSelect(id) {
    if (id === 'kp-slice-it-in-4') {
      this.setState({ isBadgeActive: true }, () => {
        setTimeout(() => this.setState({ isBadgeActive: false }), 400);
      });
    }
    this.props.onChange(id);
  }

  render() {
    const {
      onChange,
      country,
      t,
      products,
      isSelectedFn,
      isInstalledFn = () => false,
      isAddingProducts = false,
      isKlarnaOneAvailable = false,
      extraAttributesToProductFn,
    } = this.props;

    const flattenedProducts = Products.generateProductsForState(
      country,
      products,
      isSelectedFn,
      isInstalledFn,
    );
    const checkout = Products.generateFilteredProducts(
      flattenedProducts,
      t,
      'kco',
      country,
      isAddingProducts,
      isKlarnaOneAvailable,
      false,
      extraAttributesToProductFn,
    );

    const payments = Products.generateFilteredProducts(
      flattenedProducts,
      t,
      'kp',
      country,
      isAddingProducts,
      isKlarnaOneAvailable,
      this.state.isBadgeActive,
      extraAttributesToProductFn,
    );

    return (
      <div>
        {payments.length > 0 && (
          <>
            <VerticalRhythm spacing={5} />
            {country !== 'US' && (
              <ParagraphSecondary>{t(`${tPrefix}.products_block.title_2`)}</ParagraphSecondary>
            )}
            <VerticalRhythm spacing={2} />
            <SelectorDirect
              onSelect={isAddingProducts ? () => {} : this.onSelect}
              options={payments}
            />
          </>
        )}
        {payments.length && checkout.length ? <div style={styles.grayLine} /> : null}
        {checkout.length > 0 && (
          <>
            <VerticalRhythm spacing={7} />
            <ParagraphSecondary>{t(`${tPrefix}.products_block.title_1`)}</ParagraphSecondary>
            <VerticalRhythm spacing={2} />
            <SelectorDirect onSelect={isAddingProducts ? () => {} : onChange} options={checkout} />
          </>
        )}
        {country !== 'US' && (
          <>
            {/* TODO keep this until translations for per_transaction are ready */}
            <VerticalRhythm spacing={7} />
            <ParagraphQuaternary light>{t(`${tPrefix}.products_block.note`)}</ParagraphQuaternary>
          </>
        )}
      </div>
    );
  }
}

Products.propTypes = {
  extraAttributesToProductFn: PropTypes.func.isRequired,
  isSelectedFn: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  country: PropTypes.string,
  isAddingProducts: PropTypes.bool,
  isInstalledFn: PropTypes.func,
  isKlarnaOneAvailable: PropTypes.bool,
  products: PropTypes.array,
};

export default Products;
